import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { Add, CloudUpload, Edit, Send, Visibility } from '@mui/icons-material';
import FormDrawer from '../../components/ui/FormDrawer';
import PageShell from '../../components/layout/PageShell';
import api from '../../services/api';
import { listZones } from '../../services/zoneService';
import { getSettings, bulkUpsertSettings } from '../../services/platform/settingsService';
import { resolveAssetUrl } from '../../utils/assetUrl';
import {
  defaultSampleVars,
  previewPushTemplate,
  type PushTemplateRow,
} from '../../utils/pushTemplatePreview';

type Channel = 'all' | 'push' | 'in_app' | 'sms' | 'email' | 'realtime';
type ModuleFilter = 'all' | 'transport' | 'porter' | 'on_demand';
type View = 'templates' | 'broadcast' | 'setup';
type Audience = 'all' | 'customers' | 'drivers' | 'delivery_partners' | 'zone' | 'users';

type PersonOption = { _id: string; label: string; userType?: string };

const MODULES: { key: ModuleFilter; label: string }[] = [
  { key: 'all', label: 'All modules' },
  { key: 'transport', label: 'Transport' },
  { key: 'porter', label: 'Porter' },
  { key: 'on_demand', label: 'On-demand' },
];

const CHANNELS: { key: Channel; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'push', label: 'Push' },
  { key: 'in_app', label: 'In-app' },
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
  { key: 'realtime', label: 'Realtime' },
];

const CHANNEL_LABEL: Record<string, string> = {
  push: 'Push',
  in_app: 'In-app',
  sms: 'SMS',
  email: 'Email',
  realtime: 'Realtime',
};

const EMPTY_FORM = {
  name: '',
  code: '',
  channel: 'push',
  subject: '',
  body: '',
  variables: '',
  isActive: true,
};

const headCellSx = {
  fontWeight: 650,
  fontSize: '0.68rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: 'action.hover',
  color: 'text.secondary',
  py: 1.35,
} as const;

function personLabel(u: { firstName?: string; lastName?: string; phone?: string; email?: string }) {
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return [name || 'User', u.phone || u.email].filter(Boolean).join(' · ');
}

function PushPreviewCard({ title, body, image }: { title: string; body: string; image?: string }) {
  const theme = useTheme();
  const src = image ? resolveAssetUrl(image) : '';
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        p: 1.75,
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.75,
            bgcolor: 'primary.main',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 11,
            flexShrink: 0,
          }}
        >
          App
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography fontWeight={700} noWrap sx={{ fontSize: '0.875rem' }}>
            {title || 'Title'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, whiteSpace: 'pre-wrap' }}>
            {body || 'Message body'}
          </Typography>
          {src && (
            <Box
              component="img"
              src={src}
              alt=""
              sx={{ mt: 1, width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 1.5 }}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}

function TemplatePreviewDialog({
  open, row, vars, onClose, onVarsChange,
}: {
  open: boolean;
  row: PushTemplateRow | null;
  vars: Record<string, string>;
  onClose: () => void;
  onVarsChange: (next: Record<string, string>) => void;
}) {
  if (!row) return null;
  const message = previewPushTemplate(row, vars);
  const varKeys = [...new Set([...(row.variables || []), ...Object.keys(vars)])];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{row.name || row.code}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {varKeys.length > 0 && (
            <Grid container spacing={1.25}>
              {varKeys.map((key) => (
                <Grid item xs={12} sm={6} key={key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={key}
                    value={vars[key] ?? ''}
                    onChange={(e) => onVarsChange({ ...vars, [key]: e.target.value })}
                  />
                </Grid>
              ))}
            </Grid>
          )}
          <PushPreviewCard title={message.title} body={message.body} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function NotificationTemplatesPage() {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [view, setView] = useState<View>('templates');
  const [channelFilter, setChannelFilter] = useState<Channel>('all');
  const [moduleFilter, setModuleFilter] = useState<ModuleFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [previewRow, setPreviewRow] = useState<PushTemplateRow | null>(null);
  const [previewVars, setPreviewVars] = useState<Record<string, string>>({});
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<PushTemplateRow | null>(null);
  const [templateForm, setTemplateForm] = useState(EMPTY_FORM);
  const [pushForm, setPushForm] = useState({
    title: '',
    body: '',
    audience: 'all' as Audience,
    zoneId: '',
    channels: { push: true, in_app: true },
    image: '',
  });
  const [selectedUsers, setSelectedUsers] = useState<PersonOption[]>([]);
  const [selectedDrivers, setSelectedDrivers] = useState<PersonOption[]>([]);
  const [userQuery, setUserQuery] = useState('');
  const [driverQuery, setDriverQuery] = useState('');
  const [broadcastResult, setBroadcastResult] = useState<{ sent: number; recipients?: number } | null>(null);
  const [setupForm, setSetupForm] = useState({
    fcm_server_key: '',
    web_push_vapid_public_key: '',
    web_push_vapid_private_key: '',
    web_push_subject: 'mailto:admin@supersaas.local',
    push_enabled: 'true',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['notification-templates', channelFilter, moduleFilter, search, page, pageSize],
    queryFn: async () => {
      const res = await api.get('/notifications/admin/templates', {
        params: {
          page: page + 1,
          limit: pageSize,
          channel: channelFilter === 'all' ? undefined : channelFilter,
          module: moduleFilter === 'all' ? undefined : moduleFilter,
          search: search.trim() || undefined,
        },
      });
      return res.data as {
        data: PushTemplateRow[];
        meta: { total: number; counts?: Record<string, number> };
      };
    },
  });

  const { data: zonesData } = useQuery({
    queryKey: ['zones-notify'],
    queryFn: () => listZones({ limit: 200 }),
  });

  const { data: userOptions = [] } = useQuery({
    queryKey: ['notify-user-search', userQuery],
    queryFn: async () => {
      const res = await api.get('/admin/users', {
        params: { search: userQuery, userType: 'customer', limit: 20, page: 1 },
      });
      return ((res.data.data || []) as Array<{ _id: string; firstName?: string; lastName?: string; phone?: string; email?: string; userType?: string }>)
        .map((u) => ({ _id: u._id, label: personLabel(u), userType: u.userType }));
    },
    enabled: view === 'broadcast' && pushForm.audience === 'users' && userQuery.trim().length >= 2,
  });

  const { data: driverOptions = [] } = useQuery({
    queryKey: ['notify-driver-search', driverQuery],
    queryFn: async () => {
      const [drivers, partners] = await Promise.all([
        api.get('/admin/users', { params: { search: driverQuery, userType: 'driver', limit: 12, page: 1 } }),
        api.get('/admin/users', { params: { search: driverQuery, userType: 'delivery_partner', limit: 12, page: 1 } }),
      ]);
      const rows = [...(drivers.data.data || []), ...(partners.data.data || [])] as Array<{
        _id: string; firstName?: string; lastName?: string; phone?: string; email?: string; userType?: string;
      }>;
      const seen = new Set<string>();
      return rows.filter((u) => {
        if (seen.has(u._id)) return false;
        seen.add(u._id);
        return true;
      }).map((u) => ({ _id: u._id, label: `${personLabel(u)} · ${u.userType === 'delivery_partner' ? 'Delivery' : 'Driver'}`, userType: u.userType }));
    },
    enabled: view === 'broadcast' && pushForm.audience === 'users' && driverQuery.trim().length >= 2,
  });

  const { data: pushConfig } = useQuery({
    queryKey: ['notification-push-config'],
    queryFn: async () => {
      const res = await api.get('/notifications/admin/push-config');
      return res.data.data as {
        fcm: { configured: boolean; mode: string; hasServerKey: boolean };
        webPush: { configured: boolean; source: string; publicKey?: string; subject?: string };
      };
    },
  });

  const { data: settingsRows } = useQuery({
    queryKey: ['settings', 'notification'],
    queryFn: () => getSettings('notification'),
  });

  useEffect(() => {
    if (!settingsRows) return;
    const map: Record<string, string> = {};
    for (const row of settingsRows) map[row.key] = row.value || '';
    setSetupForm((prev) => ({
      ...prev,
      fcm_server_key: map.fcm_server_key || '',
      web_push_vapid_public_key: map.web_push_vapid_public_key || pushConfig?.webPush.publicKey || '',
      web_push_vapid_private_key: map.web_push_vapid_private_key || '',
      web_push_subject: map.web_push_subject || pushConfig?.webPush.subject || prev.web_push_subject,
      push_enabled: map.push_enabled || 'true',
    }));
  }, [settingsRows, pushConfig]);

  const audienceToUserTypes: Record<string, string[] | undefined> = {
    all: undefined,
    customers: ['customer'],
    drivers: ['driver'],
    delivery_partners: ['delivery_partner'],
    zone: undefined,
    users: undefined,
  };

  const broadcastMut = useMutation({
    mutationFn: async () => {
      const channels: string[] = [];
      if (pushForm.channels.push) channels.push('push');
      if (pushForm.channels.in_app) channels.push('in_app');
      if (!channels.length) throw new Error('Select at least one channel');
      const pickedIds = [...selectedUsers, ...selectedDrivers].map((p) => p._id);
      if (pushForm.audience === 'users' && !pickedIds.length) {
        throw new Error('Select at least one user or driver');
      }
      if (pushForm.audience === 'zone' && !pushForm.zoneId) {
        throw new Error('Select a zone');
      }
      const res = await api.post('/notifications/admin/broadcast', {
        title: pushForm.title,
        body: pushForm.body,
        image: pushForm.image || undefined,
        zoneId: pushForm.audience === 'zone' ? pushForm.zoneId : undefined,
        userTypes: audienceToUserTypes[pushForm.audience],
        userIds: pushForm.audience === 'users' ? pickedIds : undefined,
        channels,
      });
      return res.data.data as { sent: number; recipients?: number };
    },
    onSuccess: (result) => {
      setBroadcastResult(result);
      setPushForm((prev) => ({ ...prev, title: '', body: '', image: '' }));
      setSelectedUsers([]);
      setSelectedDrivers([]);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const saveTemplateMut = useMutation({
    mutationFn: async () => {
      const variables = templateForm.variables.split(',').map((v) => v.trim()).filter(Boolean);
      const payload = {
        name: templateForm.name,
        code: templateForm.code,
        channel: templateForm.channel,
        subject: templateForm.subject,
        body: templateForm.body,
        variables,
        isActive: templateForm.isActive,
      };
      if (editing?._id) return api.put(`/notifications/admin/templates/${editing._id}`, payload);
      return api.post('/notifications/admin/templates', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      setEditOpen(false);
      setEditing(null);
      setTemplateForm(EMPTY_FORM);
    },
  });

  const seedMut = useMutation({
    mutationFn: async () => {
      const res = await api.post('/notifications/admin/templates/seed');
      return res.data.data as { created: number; existing: number; total: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
    },
  });

  const saveSetupMut = useMutation({
    mutationFn: () => bulkUpsertSettings([
      { category: 'notification', key: 'fcm_server_key', value: setupForm.fcm_server_key },
      { category: 'notification', key: 'web_push_vapid_public_key', value: setupForm.web_push_vapid_public_key },
      { category: 'notification', key: 'web_push_vapid_private_key', value: setupForm.web_push_vapid_private_key },
      { category: 'notification', key: 'web_push_subject', value: setupForm.web_push_subject },
      { category: 'notification', key: 'push_enabled', value: setupForm.push_enabled },
    ]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'notification'] });
      queryClient.invalidateQueries({ queryKey: ['notification-push-config'] });
    },
  });

  const generateKeysMut = useMutation({
    mutationFn: async () => {
      const res = await api.post('/notifications/admin/web-push/generate-keys');
      return res.data.data as { publicKey: string; privateKey: string };
    },
    onSuccess: (keys) => {
      setSetupForm((prev) => ({
        ...prev,
        web_push_vapid_public_key: keys.publicKey,
        web_push_vapid_private_key: keys.privateKey,
      }));
    },
  });

  const uploadImageMut = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      form.append('folder', 'notifications');
      const res = await api.post('/admin/files/upload', form);
      return res.data.data.url as string;
    },
    onSuccess: (url) => setPushForm((prev) => ({ ...prev, image: url })),
  });

  const openCreate = () => {
    setEditing(null);
    setTemplateForm(EMPTY_FORM);
    setEditOpen(true);
  };

  const openEdit = (row: PushTemplateRow) => {
    setEditing(row);
    setTemplateForm({
      name: row.name || '',
      code: row.code || '',
      channel: row.channel || 'push',
      subject: row.subject || '',
      body: row.body || '',
      variables: (row.variables || []).join(', '),
      isActive: row.isActive !== false,
    });
    setEditOpen(true);
  };

  const rows = data?.data || [];
  const total = data?.meta?.total ?? 0;
  const counts = data?.meta?.counts || {};

  const livePreview = useMemo(
    () => previewPushTemplate(
      {
        name: templateForm.name,
        code: templateForm.code,
        subject: templateForm.subject,
        body: templateForm.body,
        variables: templateForm.variables.split(',').map((v) => v.trim()).filter(Boolean),
      },
      defaultSampleVars(
        templateForm.code,
        templateForm.variables.split(',').map((v) => v.trim()).filter(Boolean),
      ),
    ),
    [templateForm],
  );

  return (
    <PageShell maxWidth={false}>
      <Box
        sx={{
          borderRadius: 2.5,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          minHeight: 'calc(100vh - 96px)',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: { xs: 1.5, md: 2 }, borderBottom: 1, borderColor: 'divider' }}
        >
          <Stack direction="row" spacing={0.25}>
            {([
              { key: 'templates', label: 'Templates' },
              { key: 'broadcast', label: 'Broadcast' },
              { key: 'setup', label: 'Push setup' },
            ] as const).map((item) => {
              const selected = view === item.key;
              return (
                <Box
                  key={item.key}
                  component="button"
                  type="button"
                  onClick={() => setView(item.key)}
                  sx={{
                    all: 'unset',
                    cursor: 'pointer',
                    px: 1.75,
                    py: 1.6,
                    fontSize: '0.875rem',
                    fontWeight: selected ? 700 : 550,
                    color: selected ? 'primary.main' : 'text.secondary',
                    borderBottom: 2,
                    borderColor: selected ? 'primary.main' : 'transparent',
                    mb: '-1px',
                  }}
                >
                  {item.label}
                </Box>
              );
            })}
          </Stack>

          {view === 'templates' && (
            <Stack direction="row" spacing={0.75}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => seedMut.mutate()}
                disabled={seedMut.isPending}
                sx={{ textTransform: 'none', fontWeight: 650, borderRadius: 1.5, height: 36 }}
              >
                {seedMut.isPending ? 'Seeding…' : 'Seed templates'}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<Add />}
                onClick={openCreate}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, height: 36, boxShadow: 'none' }}
              >
                Add template
              </Button>
            </Stack>
          )}
        </Stack>

        {view === 'templates' && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            Action-wise push codes (foreground + background). Filter by module, then Seed templates after deploy.
            <br />
            <strong>Transport:</strong> ride_offer, ride_assigned, ride_accepted, ride_arrived, ride_started, ride_completed, ride_cancelled, ride_no_drivers, ride_withdrawn, ride_partner_arrived, ride_partner_started, bid_accepted
            <br />
            <strong>Porter:</strong> porter_offer, porter_created, porter_accepted, porter_arriving, porter_reached_pickup, porter_loading, porter_started, porter_reached_destination, porter_unloading, porter_completed, porter_cancelled, porter_no_drivers
            <br />
            <strong>On-demand:</strong> on_demand_offer, on_demand_booked, on_demand_assigned, on_demand_en_route, on_demand_arrived, on_demand_started, on_demand_completed, on_demand_cancelled, on_demand_rejected, on_demand_no_providers
          </Alert>
        )}

        {seedMut.isSuccess && (
          <Alert severity="success" sx={{ borderRadius: 0 }} onClose={() => seedMut.reset()}>
            Seeded {seedMut.data?.created ?? 0} new templates ({seedMut.data?.existing ?? 0} already existed).
          </Alert>
        )}

        {view === 'templates' && (
          <Box>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              sx={{ px: { xs: 1.75, md: 2.25 }, py: 1.5 }}
            >
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                {MODULES.map((mod) => {
                  const selected = moduleFilter === mod.key;
                  return (
                    <Chip
                      key={mod.key}
                      label={mod.label}
                      clickable
                      onClick={() => { setModuleFilter(mod.key); setPage(0); }}
                      sx={{
                        height: 28,
                        fontWeight: 650,
                        fontSize: '0.75rem',
                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : alpha('#0f172a', 0.04),
                        color: selected ? 'primary.main' : 'text.secondary',
                        border: 1,
                        borderColor: selected ? alpha(theme.palette.primary.main, 0.28) : 'transparent',
                      }}
                    />
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                {CHANNELS.map((ch) => {
                  const count = counts[ch.key];
                  const selected = channelFilter === ch.key;
                  return (
                    <Chip
                      key={ch.key}
                      label={count != null ? `${ch.label} ${count}` : ch.label}
                      clickable
                      onClick={() => { setChannelFilter(ch.key); setPage(0); }}
                      sx={{
                        height: 28,
                        fontWeight: 650,
                        fontSize: '0.75rem',
                        bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : alpha('#0f172a', 0.04),
                        color: selected ? 'primary.main' : 'text.secondary',
                        border: 1,
                        borderColor: selected ? alpha(theme.palette.primary.main, 0.28) : 'transparent',
                      }}
                    />
                  );
                })}
              </Stack>
              <TextField
                size="small"
                placeholder="Search templates"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                sx={{ width: { xs: '100%', sm: 220 }, '& .MuiOutlinedInput-root': { height: 36, borderRadius: 1.5 } }}
              />
            </Stack>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: 52 }}>#</TableCell>
                    <TableCell sx={headCellSx}>Template</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 110 }}>Channel</TableCell>
                    <TableCell sx={{ ...headCellSx, display: { xs: 'none', md: 'table-cell' } }}>Title</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 88 }}>Status</TableCell>
                    <TableCell sx={{ ...headCellSx, width: 96 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={row._id || `${row.code}-${row.channel}`}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}
                      onClick={() => openEdit(row)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          {page * pageSize + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{row.name}</Typography>
                        <Typography variant="caption" color="text.disabled" fontFamily="monospace">{row.code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={CHANNEL_LABEL[row.channel || ''] || row.channel} sx={{ height: 22, fontWeight: 650, fontSize: '0.68rem' }} />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 360 }}>
                          {row.subject || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.isActive === false ? 'Off' : 'On'}
                          color={row.isActive === false ? 'default' : 'success'}
                          variant="outlined"
                          sx={{ height: 22, fontWeight: 700, fontSize: '0.65rem' }}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPreviewRow(row);
                              setPreviewVars(defaultSampleVars(row.code || '', row.variables || []));
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!isLoading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 7, textAlign: 'center' }}>
                        <Typography color="text.secondary">No templates yet — seed the catalog to start</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        )}

        {view === 'broadcast' && (
          <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 680 }}>
            <Typography fontWeight={750} sx={{ fontSize: '1.05rem', letterSpacing: '-0.03em', mb: 2 }}>
              Send a notification
            </Typography>

            <TextField
              fullWidth
              label="Title"
              value={pushForm.title}
              onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })}
              sx={{ mb: 1.75 }}
            />
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={4}
              value={pushForm.body}
              onChange={(e) => setPushForm({ ...pushForm, body: e.target.value })}
              sx={{ mb: 1.75 }}
            />
            <TextField
              select
              fullWidth
              label="Audience"
              value={pushForm.audience}
              onChange={(e) => setPushForm({ ...pushForm, audience: e.target.value as Audience })}
              sx={{ mb: 1.75 }}
            >
              <MenuItem value="all">All users</MenuItem>
              <MenuItem value="customers">Customers</MenuItem>
              <MenuItem value="drivers">Drivers</MenuItem>
              <MenuItem value="delivery_partners">Delivery partners</MenuItem>
              <MenuItem value="zone">Zone</MenuItem>
              <MenuItem value="users">Specific users / drivers</MenuItem>
            </TextField>

            {pushForm.audience === 'zone' && (
              <TextField
                select
                fullWidth
                label="Zone"
                value={pushForm.zoneId}
                onChange={(e) => setPushForm({ ...pushForm, zoneId: e.target.value })}
                sx={{ mb: 1.75 }}
              >
                {(zonesData?.rows || []).map((z) => (
                  <MenuItem key={z._id} value={z._id}>{z.name}</MenuItem>
                ))}
              </TextField>
            )}

            {pushForm.audience === 'users' && (
              <Stack spacing={1.5} sx={{ mb: 1.75 }}>
                <Autocomplete
                  multiple
                  options={userOptions}
                  value={selectedUsers}
                  onChange={(_, v) => setSelectedUsers(v)}
                  onInputChange={(_, v) => setUserQuery(v)}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  renderInput={(params) => <TextField {...params} label="Customers" placeholder="Search name or phone" />}
                />
                <Autocomplete
                  multiple
                  options={driverOptions}
                  value={selectedDrivers}
                  onChange={(_, v) => setSelectedDrivers(v)}
                  onInputChange={(_, v) => setDriverQuery(v)}
                  getOptionLabel={(o) => o.label}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  renderInput={(params) => <TextField {...params} label="Drivers / delivery partners" placeholder="Search name or phone" />}
                />
              </Stack>
            )}

            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
              disabled={uploadImageMut.isPending}
              sx={{ textTransform: 'none', fontWeight: 650, borderRadius: 1.5, mb: 1.75 }}
            >
              {uploadImageMut.isPending ? 'Uploading…' : pushForm.image ? 'Change image' : 'Attach image'}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadImageMut.mutate(file);
                  e.target.value = '';
                }}
              />
            </Button>
            {pushForm.image && (
              <Button size="small" onClick={() => setPushForm({ ...pushForm, image: '' })} sx={{ ml: 1, mb: 1.75, textTransform: 'none' }}>
                Remove image
              </Button>
            )}

            <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
              <Chip
                label="Mobile / web push"
                clickable
                onClick={() => setPushForm({ ...pushForm, channels: { ...pushForm.channels, push: !pushForm.channels.push } })}
                sx={{
                  fontWeight: 650,
                  bgcolor: pushForm.channels.push ? alpha(theme.palette.primary.main, 0.12) : undefined,
                  color: pushForm.channels.push ? 'primary.main' : 'text.secondary',
                }}
              />
              <Chip
                label="In-app"
                clickable
                onClick={() => setPushForm({ ...pushForm, channels: { ...pushForm.channels, in_app: !pushForm.channels.in_app } })}
                sx={{
                  fontWeight: 650,
                  bgcolor: pushForm.channels.in_app ? alpha(theme.palette.primary.main, 0.12) : undefined,
                  color: pushForm.channels.in_app ? 'primary.main' : 'text.secondary',
                }}
              />
            </Stack>

            {(pushForm.title || pushForm.body || pushForm.image) && (
              <Box sx={{ mb: 2.5 }}>
                <PushPreviewCard title={pushForm.title} body={pushForm.body} image={pushForm.image} />
              </Box>
            )}

            <Button
              variant="contained"
              startIcon={<Send />}
              disabled={!pushForm.title || !pushForm.body || broadcastMut.isPending}
              onClick={() => broadcastMut.mutate()}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, height: 40, boxShadow: 'none' }}
            >
              {broadcastMut.isPending ? 'Sending…' : 'Send'}
            </Button>
            {broadcastResult && (
              <Typography variant="body2" color="success.main" sx={{ mt: 1.25 }}>
                Sent {broadcastResult.sent} notification{broadcastResult.sent === 1 ? '' : 's'}
                {broadcastResult.recipients != null ? ` to ${broadcastResult.recipients} recipient${broadcastResult.recipients === 1 ? '' : 's'}` : ''}
              </Typography>
            )}
            {broadcastMut.isError && (
              <Typography variant="body2" color="error.main" sx={{ mt: 1.25 }}>
                {(broadcastMut.error as Error)?.message || 'Broadcast failed'}
              </Typography>
            )}
          </Box>
        )}

        {view === 'setup' && (
          <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 680 }}>
            <Stack spacing={2.5}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography fontWeight={750}>Firebase push</Typography>
                  <Chip
                    size="small"
                    label={pushConfig?.fcm.configured ? `On · ${pushConfig.fcm.mode}` : 'Not configured'}
                    color={pushConfig?.fcm.configured ? 'success' : 'default'}
                    sx={{ height: 22, fontWeight: 700 }}
                  />
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={setupForm.push_enabled === 'true'}
                      onChange={(e) => setSetupForm({ ...setupForm, push_enabled: e.target.checked ? 'true' : 'false' })}
                    />
                  }
                  label="Enable push notifications"
                />
                <TextField
                  fullWidth
                  type="password"
                  label="FCM server key"
                  value={setupForm.fcm_server_key}
                  onChange={(e) => setSetupForm({ ...setupForm, fcm_server_key: e.target.value })}
                  helperText="Used when Firebase Admin SDK (v1) is not available on the server"
                  sx={{ mt: 1.25 }}
                />
              </Box>

              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography fontWeight={750}>Web push</Typography>
                  <Chip
                    size="small"
                    label={pushConfig?.webPush.configured ? `On · ${pushConfig.webPush.source}` : 'Not configured'}
                    color={pushConfig?.webPush.configured ? 'success' : 'default'}
                    sx={{ height: 22, fontWeight: 700 }}
                  />
                </Stack>
                <TextField
                  fullWidth
                  label="VAPID public key"
                  value={setupForm.web_push_vapid_public_key}
                  onChange={(e) => setSetupForm({ ...setupForm, web_push_vapid_public_key: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  type="password"
                  label="VAPID private key"
                  value={setupForm.web_push_vapid_private_key}
                  onChange={(e) => setSetupForm({ ...setupForm, web_push_vapid_private_key: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth
                  label="VAPID subject"
                  value={setupForm.web_push_subject}
                  onChange={(e) => setSetupForm({ ...setupForm, web_push_subject: e.target.value })}
                  sx={{ mb: 1.5 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => generateKeysMut.mutate()}
                  disabled={generateKeysMut.isPending}
                  sx={{ textTransform: 'none', fontWeight: 650, borderRadius: 1.5 }}
                >
                  {generateKeysMut.isPending ? 'Generating…' : 'Generate VAPID keys'}
                </Button>
              </Box>

              <Box>
                <Button
                  variant="contained"
                  onClick={() => saveSetupMut.mutate()}
                  disabled={saveSetupMut.isPending}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, boxShadow: 'none' }}
                >
                  {saveSetupMut.isPending ? 'Saving…' : 'Save setup'}
                </Button>
                {saveSetupMut.isSuccess && (
                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>Saved</Typography>
                )}
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      <FormDrawer
        open={editOpen}
        title={editing ? 'Edit template' : 'New template'}
        onClose={() => { setEditOpen(false); setEditing(null); }}
        onSubmit={() => saveTemplateMut.mutate()}
        submitLabel={editing ? 'Save' : 'Create'}
        loading={saveTemplateMut.isPending}
        disableSubmit={!templateForm.name || !templateForm.code || !templateForm.body}
        width={560}
      >
        <Stack spacing={2}>
          <Grid container spacing={1.75}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name" value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Event code" value={templateForm.code} onChange={(e) => setTemplateForm({ ...templateForm, code: e.target.value })} disabled={!!editing} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Channel" value={templateForm.channel} onChange={(e) => setTemplateForm({ ...templateForm, channel: e.target.value })} disabled={!!editing}>
                <MenuItem value="push">Push</MenuItem>
                <MenuItem value="in_app">In-app</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="realtime">Realtime</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={<Switch checked={templateForm.isActive} onChange={(e) => setTemplateForm({ ...templateForm, isActive: e.target.checked })} />}
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Title" value={templateForm.subject} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Body"
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                helperText="Use {{variable}} for dynamic values"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Variables"
                value={templateForm.variables}
                onChange={(e) => setTemplateForm({ ...templateForm, variables: e.target.value })}
                placeholder="rideNumber, firstName"
              />
            </Grid>
          </Grid>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 650 }}>Preview</Typography>
            <PushPreviewCard title={livePreview.title} body={livePreview.body} />
          </Box>
        </Stack>
      </FormDrawer>

      <TemplatePreviewDialog
        open={previewRow != null}
        row={previewRow}
        vars={previewVars}
        onClose={() => setPreviewRow(null)}
        onVarsChange={setPreviewVars}
      />
    </PageShell>
  );
}
